import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { useServices } from "@/hooks/useServices";
import FilterPanel from "../components/FilterPanel";
import ServiceCard from "../components/ServiceCard";

interface ServicesSectionProps {
  filters?: Record<string, any>;
  onFiltersChange?: (filters: Record<string, any>) => void;
}

export default function ServicesSection({ filters, onFiltersChange }: ServicesSectionProps) {
  const searchParams = new URLSearchParams(useSearch());
  const { data: services, loading, error } = useServices();

  // State for selected service
  const [selectedServiceId, setSelectedServiceId] = useState<string | undefined>();

  // Check URL parameters for selected service
  useEffect(() => {
    const serviceIdFromUrl = searchParams.get('serviceId');
    if (serviceIdFromUrl) {
      setSelectedServiceId(serviceIdFromUrl);
    }
  }, [searchParams]);

  // Frontend filtering
  const filteredServices = (services || []).filter(service => {
    // Search filter
    if (filters?.search) {
      const searchTerm = filters.search.toLowerCase();
      const matchesName = service.name?.toLowerCase().includes(searchTerm);
      const matchesDescription = service.description?.toLowerCase().includes(searchTerm);
      const matchesNotes = service.notes?.toLowerCase().includes(searchTerm);
      if (!matchesName && !matchesDescription && !matchesNotes) {
        return false;
      }
    }

    // Vendor filter
    if (filters?.vendorId && service.vendorId !== filters.vendorId) {
      return false;
    }

    // Service type filter
    if (filters?.serviceTypeId && service.serviceTypeId !== filters.serviceTypeId) {
      return false;
    }

    // Service category filter
    if (filters?.serviceCategoryId && service.serviceCategoryId !== filters.serviceCategoryId) {
      return false;
    }

    // Service subcategory filter
    if (filters?.serviceSubCategoryId && service.serviceSubCategoryId !== filters.serviceSubCategoryId) {
      return false;
    }

    // Active/inactive filter
    if (filters?.isActive !== undefined && service.isActive !== filters.isActive) {
      return false;
    }

    return true;
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex gap-6">
        {/* Left Sidebar - Filter Panel */}
        <div className="w-80 flex-shrink-0">
          <FilterPanel
            filters={filters || {}}
            onFiltersChange={onFiltersChange || (() => {})}
            type="services"
          />
        </div>

        {/* Main Content - Loading State */}
        <div className="flex-1">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-2">Services</h2>
            <p className="text-muted-foreground">Loading services...</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex gap-6">
        {/* Left Sidebar - Filter Panel */}
        <div className="w-80 flex-shrink-0">
          <FilterPanel
            filters={filters || {}}
            onFiltersChange={onFiltersChange || (() => {})}
            type="services"
          />
        </div>

        {/* Main Content - Error State */}
        <div className="flex-1">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-2">Services</h2>
            <p className="text-muted-foreground">Failed to load services</p>
          </div>

          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load services: {error}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      {/* Left Sidebar - Filter Panel */}
      <div className="w-80 flex-shrink-0">
        <FilterPanel
          filters={filters || {}}
          onFiltersChange={onFiltersChange || (() => {})}
          type="services"
        />
      </div>

      {/* Main Content - Services Grid */}
      <div className="flex-1">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold mb-2">Services</h2>
          <p className="text-muted-foreground">
            {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'} available
          </p>
        </div>

        {filteredServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No services found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your filters or create a new service.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}